import React, { useEffect, useState, useRef } from 'react';
import { connect, useSelector } from 'react-redux';

import { toggleModal, toggleModalWithTimer, toggleSpinner, updateMessages } from 'apps/auth/actions/appAction';
import { updateCardsList } from 'apps/auth/actions/cardsAction';
import { addExtras, deleteDCAccount } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, setNewForm } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import DeleteCardOrAccountPopup from 'apps/auth/components/cards/DeleteCardOrAccountPopup';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, PanelContent, PanelFrame, PanelRightBlock } from 'apps/auth/components/common/Panel/styles';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import Form from 'apps/auth/components/form-multi/Form';
import { getField } from 'apps/auth/components/form-multi/utils';
import SendByPlaid from 'apps/auth/components/send/Ach/plaid/SendByPlaid';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { PAYMENT_ACH } from 'apps/auth/constants/Constants';
import { AMOUNT_FROM } from 'apps/auth/constants/fieldsName';
import { URL_IMAGES_BANKS, AUTH_CARD_DELETE } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { INFO, isAccountExpired, requestPlaid } from 'apps/auth/services/achService';

const DcAchAccount = ({ badge = '', actionBanner = '', ...props }) => {
    const DELETE_ACCOUNT_FORM_NAME = 'deleteCardPopup';
    const popupRef = useRef(null);
    const [, setScreen] = useState(INFO);
    const [, setErrorText] = useState('');
    const onSuccessPlaid = requestPlaid(setScreen, setErrorText, props);

    const onDeleteAccountSubmitHandler = (fields, data) => {
        const forceError = data.extra.force;
        const successText = data?.messages?.successText;

        if (!forceError) {
            const action = props.deleteDCAccount(props.account.id, 'ACH');
            action.then(() => {
                popupRef.current?.close();
                props.toggleModalWithTimer(true, successText);
            });
        } else {
            popupRef.current?.close();
            props.toggleModal(
                true,
                <ModalComponent canCloseModal={false} isVerticalCentered>
                    <InfoPage
                        insideModalComponent={true}
                        animatedIconFail
                        mainText={forceError}
                        primaryButtonCaption={props.messages.pageMessages.goBack}
                        primaryButtonOnClick={() => props.toggleModal(false)}
                    />
                </ModalComponent>,
            );
        }
    };

    useEffect(() => {
        const nameField = getField('name', props.account.alias, '', true, false, props.placeholder, 'text');
        const cardIdField = getField('id', props.account.id);
        const force = getField('force', false);

        props.setNewForm(DELETE_ACCOUNT_FORM_NAME, {
            [nameField.name]: nameField,
            [cardIdField.name]: cardIdField,
            [force.name]: force,
        });
    }, []);

    const revokeAccessAction = (e) => {
        e.preventDefault();
        e.stopPropagation();
        popupRef.current?.open(<DeleteCardOrAccountPopup isACH={true} forDcAccount={true} selectedCard={props.account} popupRef={popupRef} />);
    };

    const { changeAmount, gotItButton, revokeAccessText, refreshAccess, dcSourceUnlinked, continueTransferFlow, changePaymentOption } = useSelector(
        ({ messages: { pageMessages } }) => pageMessages,
    );

    const isNotEnoughFunds = props.isNotEnoughFunds;
    const isBelowMinAmount = props.isBelowMinAmount;
    const isAccessRevocation = props.isAccessRevocation;
    const isAccessExpired = isAccountExpired(props.account);
    const isDisabledAccount = isNotEnoughFunds || isBelowMinAmount || isAccessExpired;

    const updateAmount = (value) => {
        props.changeFormFieldValue(props.formName, value, AMOUNT_FROM).then(() => {
            props.returnToCalculator();
        });
    };

    const onClickPopupButton = (e) => {
        e.preventDefault();
        if (isBelowMinAmount && props.returnToCalculator) {
            updateAmount(props.balance);
        } else if (isAccessExpired) {
            e.preventDefault();
            props.toggleSpinner(true);
            popupRef.current?.close();
            const fields = {};
            // eslint-disable-next-line no-undef
            fields.source_code = getField('source_code', props.account.code);
            fields.source_id = getField('source_id', props.account.id);
            fields.from_prv_id = getField('from_prv_id', props.account.fromPrvId);
            apiFetchData('/auth/send/ach-token/refresh.action', fields)
                .then((json) => {
                    if (json.redirectTo) {
                        window.location.replace(json.redirectTo);
                    } else if (json.extra && json.extra.linkToken) {
                        props.addExtras(json.extra).then(() => props.toggleModal(true, <SendByPlaid onSuccess={onSuccessPlaid} />, false));
                    }
                })
                .finally(() => props.toggleSpinner(false))
                .catch(() => {
                    props.toggleSpinner(false);
                    popupRef.current?.close();
                });
        }
        popupRef.current?.close();
    };

    const getPopup = () => {
        let icon, buttonText;
        if (isNotEnoughFunds) {
            icon = 'check';
            buttonText = gotItButton;
        } else {
            icon = isBelowMinAmount ? 'arrow-back' : 'retry';
            buttonText = isBelowMinAmount ? changeAmount : refreshAccess;
        }
        return (
            <SimplePopup text={props.popupText} popupRef={popupRef}>
                <PopupButton iconName={icon} text={buttonText} isAlertButton={isAccessRevocation} onClick={onClickPopupButton} />
                {isAccessExpired && (
                    <Form
                        id="delete_card_form_id"
                        action={AUTH_CARD_DELETE}
                        formName={DELETE_ACCOUNT_FORM_NAME}
                        validateForm={false}
                        onSubmitHandler={onDeleteAccountSubmitHandler}
                    >
                        <PopupButton iconName={'cross'} type={'submit'} isAlertButton={true} text={revokeAccessText} />
                    </Form>
                )}
            </SimplePopup>
        );
    };

    const onClickHandler = (e) => {
        if (isDisabledAccount) {
            e.preventDefault();
            popupRef.current?.open(getPopup());
        } else if (isAccessRevocation) {
            e.preventDefault();
            popupRef.current?.open(<DeleteCardOrAccountPopup isACH={true} forDcAccount={true} selectedCard={props.account} popupRef={popupRef} />);
        } else if (actionBanner) {
            e.preventDefault();
            popupRef.current?.open(
                <SimplePopup popupRef={popupRef} text={<DangerouslyHtml dangerouslyHtml={actionBanner} />}>
                    <PopupButton
                        iconName="arrow-forward"
                        text={continueTransferFlow}
                        onClick={() => {
                            props.clickHandler(e, PAYMENT_ACH, props.account.code);
                            popupRef.current?.close();
                        }}
                    />
                    <PopupButton
                        iconName="retry"
                        text={changePaymentOption}
                        onClick={() => {
                            popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
        } else if (props.clickHandler) {
            props.clickHandler(e, PAYMENT_ACH, props.account.code);
        }
    };

    const getBankIcon = (account) => {
        if (account.accountLogo) {
            return `${URL_IMAGES_BANKS}/${account.accountLogo}.png`;
        } else {
            return null;
        }
    };

    const getAltText = (account) => {
        if (account.accountLogo) {
            return account.accountLogo;
        } else {
            return null;
        }
    };

    const getIconName = (account) => {
        if (!account.accountLogo) {
            return 'bank';
        } else {
            return null;
        }
    };

    const getIconOrBadge = (paymentMethod, isAccRevocation, isNoFunds, isMinAmount, isExpired) => {
        let firstComponent = null,
            secondComponent = null;
        if (isNoFunds || isMinAmount) {
            firstComponent = <Icon name={'alert'} size={'16px'} color={'lightGray'} />;
        } else if (isExpired) {
            firstComponent = <Badge variant="tertiary" caption={dcSourceUnlinked} />;
        } else {
            if (isAccRevocation) {
                secondComponent = <DiskButton type="button" variant="error" iconName="cross" iconColor="error" onClick={revokeAccessAction} raised={false} />;
            } else if (badge) {
                firstComponent = <Badge variant="primary" caption={badge} />;
            }
        }
        return [firstComponent, secondComponent].map((el, index) => el && <span key={index}>{el}</span>);
    };

    const alias = `${props.account.alias} ·· ${props.account.description}`;
    return (
        <>
            <Panel onClick={onClickHandler} negativeMargin isHoverable>
                <PanelFrame>
                    <DiskBadgeWrapper>
                        <DiskBadge
                            isDisabled={isDisabledAccount}
                            iconName={getIconName(props.account)}
                            src={getBankIcon(props.account)}
                            alt={getAltText(props.account)}
                        />
                    </DiskBadgeWrapper>
                    <PanelContent>
                        <Text color={isDisabledAccount ? 'secondary' : 'primary'}>{alias}</Text>
                    </PanelContent>
                    <PanelRightBlock>
                        {getIconOrBadge(props.paymentMethod, isAccessRevocation, isNotEnoughFunds, isBelowMinAmount, isAccessExpired)}
                    </PanelRightBlock>
                </PanelFrame>
            </Panel>
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, {
    changeFormFieldValue,
    deleteDCAccount,
    toggleSpinner,
    addExtras,
    toggleModal,
    toggleModalWithTimer,
    updatePageMessages,
    updateMessages,
    updateCardsList,
    setNewForm,
})(DcAchAccount);
